import { workspaceContentKey } from '../../data/workspace'
import type { ContextsAccountAdapter, ContextsAccountWorkspace } from './contract'

const format = 'minimany-contexts-legacy'
function storageKeys(workspace: ContextsAccountWorkspace) {
  const scoped = (key: string) => workspaceContentKey(key, workspace.databaseName)
  return {
    draft: scoped(`minimany:context-lab-draft:v1:${workspace.owner}`),
    recovery: scoped(`minimany:context-lab-draft:v1:${workspace.owner}:recovery`),
    workingView: scoped(`minimany:context-lab-working-view:v1:${workspace.owner}`),
  }
}
function validatePayload(value: unknown): Record<'draft' | 'recovery' | 'workingView', string | null> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('Invalid Contexts recovery data.')
  const record = value as Record<string, unknown>
  for (const field of ['draft', 'recovery', 'workingView']) {
    if (record[field] !== null && typeof record[field] !== 'string') throw new Error('Invalid Contexts recovery data.')
  }
  return record as Record<'draft' | 'recovery' | 'workingView', string | null>
}

/** Replace this implementation when Contexts storage/sync is revamped. Keep old recovery readers. */
export const legacyContextsAccountAdapter: ContextsAccountAdapter = {
  async inspect(workspace) {
    // Inspection is needed only when opening sign-out, not on workspace startup/removal.
    const [{ listContextDocuments }, { equalDocument }, { readPendingContextPlacements }] = await Promise.all([
      import('../../intelligence/contextSync/store'),
      import('../../intelligence/contextSync/model'),
      import('../../intelligence/contextLab/pendingPlacements'),
    ])
    const [contexts, placements] = await Promise.all([
      listContextDocuments(workspace.owner), readPendingContextPlacements(workspace.owner),
    ])
    return { pendingChanges: contexts.filter(({ document, base }) => !equalDocument(document, base?.document ?? null)).length
      + placements.length + (localStorage.getItem(storageKeys(workspace).draft) ? 1 : 0) }
  },
  async captureRecovery(workspace) {
    return { format, version: 1, payload: Object.fromEntries(Object.entries(storageKeys(workspace)).map(([field, key]) => [field, localStorage.getItem(key)])) }
  },
  async prepareRestore(workspace, source) {
    let payload: unknown
    if (source.kind === 'legacy-workspace') {
      const data = source.document
      payload = { draft: data.contextDraft ?? null, recovery: data.contextRecovery ?? null, workingView: data.contextWorkingView ?? null }
    } else {
      const snapshot = source.snapshot as { format?: unknown; version?: unknown; payload?: unknown } | null
      if (!snapshot || snapshot.format !== format || snapshot.version !== 1) throw new Error('This Contexts recovery format requires a compatible app version. Nothing was imported.')
      payload = snapshot.payload
    }
    const data = validatePayload(payload)
    const keys = storageKeys(workspace)
    return async () => {
      for (const field of Object.keys(keys) as (keyof typeof keys)[]) {
        if (data[field] !== null && localStorage.getItem(keys[field]) === null) localStorage.setItem(keys[field], data[field])
      }
    }
  },
  isAuthoredRecord(store, key) {
    return store === 'meta' && /^(manual-context:|context-build:|context-placement:|context-sync:.*recovery:)/.test(key)
  },
  async removeOfflineContent(workspace) {
    for (const key of Object.values(storageKeys(workspace))) localStorage.removeItem(key)
  },
  async synchronize(_workspace, host) {
    await host.synchronizeCore(['contexts'])
  },
}
